import { hasOnlyTuningKeys, isTuningEnum, isTuningNumber, isTuningRecord, isTuningString } from './validation';

export type TuningValue = number | string | boolean | null;
export type TuningSettingKey =
  | 'video.codec' | 'video.maxBitrateBps' | 'video.maxFramerate'
  | 'video.resolutionTier' | 'video.degradationPreference' | 'video.temporalLayers'
  | 'video.nackRtx' | 'ice.transportPolicy'
  | 'audio.maxBitrateBps' | 'audio.stereo' | 'audio.packetTimeMs'
  | 'receiver.videoJitterBufferTargetMs' | 'receiver.videoPlayoutDelayHintMs'
  | 'receiver.audioJitterBufferTargetMs' | 'receiver.audioPlayoutDelayHintMs'
  | 'recovery.mode' | 'recovery.keyframePolicy' | 'recovery.lossKeyframeMinIntervalMs'
  | 'recovery.unhealthyHoldMs' | 'recovery.healthyHoldMs'
  | 'capture.resolutionTier' | 'capture.requestedFps'
  | 'telemetry.windowMs' | 'telemetry.statsIntervalMs' | 'telemetry.encodedFrameTap';
export type TuningPatch = Readonly<Partial<Record<TuningSettingKey, TuningValue>>>;
export type ResolvedTuningConfiguration = {
  version: 1; settings: TuningPatch; strategyId: string; configurationHash: string; policyVersion: string;
};
export type TuningApplyMode = 'live' | 'fresh-stream' | 'host-restart' | 'unsupported';
export type TuningSettingSupport = {
  mode: TuningApplyMode; scope: 'peer' | 'shared-capture' | 'process';
  nativeApi: 'supported' | 'unsupported' | 'unknown';
  values?: readonly TuningValue[]; min?: number; max?: number; reason?: string;
};
export type TuningCapabilities = {
  protocolVersion: 1; eligible: boolean; armed: boolean; endpoint: 'host' | 'guest';
  buildId: string; runtimeId: string;
  settings: Partial<Record<TuningSettingKey, TuningSettingSupport>>; strategies: readonly string[];
};

export type TuningSettingDescriptor = TuningSettingSupport & { nullable?: boolean; boolean?: boolean };
const peer = { scope: 'peer', nativeApi: 'unknown' } as const;
const live = { ...peer, mode: 'live' } as const;
const fresh = { ...peer, mode: 'fresh-stream' } as const;
const hint = { ...live, min: 0, max: 1_000, nullable: true } as const;
/** Immutable validation bounds, not an assertion that a runtime supports the native effect. */
export const TUNING_SETTING_DESCRIPTORS: Readonly<Record<TuningSettingKey, TuningSettingDescriptor>> = Object.freeze({
  'video.codec': { ...fresh, values: ['auto', 'h264', 'h265'] },
  'video.maxBitrateBps': { ...live, min: 30_000, max: 50_000_000 },
  'video.maxFramerate': { ...live, min: 1, max: 120 },
  'video.resolutionTier': { ...live, values: ['source', 'top', 'middle', 'bottom'] },
  'video.degradationPreference': { ...live, values: ['maintain-framerate', 'maintain-resolution', 'balanced'] },
  'video.temporalLayers': { ...fresh, values: ['auto', 'L1T1', 'L1T3'] },
  'video.nackRtx': { ...fresh, values: ['default', 'disabled'] },
  'ice.transportPolicy': { ...fresh, values: ['all', 'relay'] },
  'audio.maxBitrateBps': { ...live, min: 16_000, max: 320_000 },
  'audio.stereo': { ...fresh, boolean: true },
  'audio.packetTimeMs': { ...fresh, values: [10, 20] },
  'receiver.videoJitterBufferTargetMs': hint,
  'receiver.videoPlayoutDelayHintMs': hint,
  'receiver.audioJitterBufferTargetMs': hint,
  'receiver.audioPlayoutDelayHintMs': hint,
  'recovery.mode': { ...live, values: ['production', 'fixed-application'] },
  'recovery.keyframePolicy': { ...live, values: ['production', 'required-only', 'loss-repair'] },
  'recovery.lossKeyframeMinIntervalMs': { ...live, min: 0, max: 30_000 },
  'recovery.unhealthyHoldMs': { ...live, min: 250, max: 10_000 },
  'recovery.healthyHoldMs': { ...live, min: 1_000, max: 120_000 },
  'capture.resolutionTier': { ...fresh, scope: 'shared-capture', values: ['unchanged', 'top', 'middle', 'bottom'] },
  'capture.requestedFps': { ...live, scope: 'shared-capture', min: 1, max: 240, nullable: true },
  'telemetry.windowMs': { ...live, values: [250, 500, 1_000] },
  'telemetry.statsIntervalMs': { ...live, values: [250, 500, 1_000] },
  'telemetry.encodedFrameTap': { ...fresh, boolean: true },
});
export const TUNING_SETTING_KEYS = Object.freeze(Object.keys(TUNING_SETTING_DESCRIPTORS) as TuningSettingKey[]);
export const TUNING_STRATEGY_IDS = Object.freeze(['production', 'required-keyframes', 'loss-repair', 'minimal-instrumentation']);
for (const descriptor of Object.values(TUNING_SETTING_DESCRIPTORS)) {
  if (descriptor.values) Object.freeze(descriptor.values);
  Object.freeze(descriptor);
}

export function isTuningSettingKey(value: unknown): value is TuningSettingKey {
  return typeof value === 'string' && Object.prototype.hasOwnProperty.call(TUNING_SETTING_DESCRIPTORS, value);
}

export function isTuningSettingValue(key: TuningSettingKey, value: unknown): value is TuningValue {
  const descriptor = TUNING_SETTING_DESCRIPTORS[key];
  if (value === null) return descriptor.nullable === true;
  if (descriptor.boolean) return typeof value === 'boolean';
  if (descriptor.values) return descriptor.values.includes(value as TuningValue);
  return isTuningNumber(value, descriptor.min, descriptor.max);
}

export function isTuningPatch(value: unknown): value is TuningPatch {
  return isTuningRecord(value) && Object.entries(value).every(([key, setting]) =>
    isTuningSettingKey(key) && isTuningSettingValue(key, setting));
}

export function parseTuningPatch(value: unknown): TuningPatch | undefined {
  return isTuningPatch(value) ? value : undefined;
}

export function isTuningConfigurationHash(value: unknown): value is string {
  return typeof value === 'string' && /^sha256:[a-f0-9]{64}$/.test(value);
}

export function isResolvedTuningConfiguration(value: unknown): value is ResolvedTuningConfiguration {
  return isTuningRecord(value) && hasOnlyTuningKeys(value,
    ['version', 'settings', 'strategyId', 'configurationHash', 'policyVersion']) && value['version'] === 1 &&
    isTuningPatch(value['settings']) && isTuningString(value['strategyId']) && TUNING_STRATEGY_IDS.includes(value['strategyId']) &&
    isTuningConfigurationHash(value['configurationHash']) && isTuningString(value['policyVersion']);
}

export function isTuningSettingSupport(value: unknown): value is TuningSettingSupport {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, ['mode', 'scope', 'nativeApi', 'values', 'min', 'max', 'reason'])) return false;
  if (!isTuningEnum(value['mode'], ['live', 'fresh-stream', 'host-restart', 'unsupported']) ||
      !isTuningEnum(value['scope'], ['peer', 'shared-capture', 'process']) ||
      !isTuningEnum(value['nativeApi'], ['supported', 'unsupported', 'unknown'])) return false;
  if (value['values'] !== undefined && (!Array.isArray(value['values']) || value['values'].length > 16 ||
      !value['values'].every((item) => item === null || typeof item === 'boolean' || isTuningString(item) || isTuningNumber(item)))) return false;
  return (value['min'] === undefined || isTuningNumber(value['min'])) &&
    (value['max'] === undefined || isTuningNumber(value['max'])) &&
    !(typeof value['min'] === 'number' && typeof value['max'] === 'number' && value['min'] > value['max']) &&
    (value['reason'] === undefined || isTuningString(value['reason'], 2_048));
}

export function isTuningCapabilities(value: unknown): value is TuningCapabilities {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value,
    ['protocolVersion', 'eligible', 'armed', 'endpoint', 'buildId', 'runtimeId', 'settings', 'strategies'])) return false;
  return value['protocolVersion'] === 1 && typeof value['eligible'] === 'boolean' && typeof value['armed'] === 'boolean' &&
    !(value['armed'] && !value['eligible']) && isTuningEnum(value['endpoint'], ['host', 'guest']) &&
    isTuningString(value['buildId'], 256) && isTuningString(value['runtimeId']) && isTuningRecord(value['settings']) &&
    Object.entries(value['settings']).every(([key, support]) => isTuningSettingKey(key) && isTuningSettingSupport(support)) &&
    Array.isArray(value['strategies']) && value['strategies'].length <= TUNING_STRATEGY_IDS.length &&
    value['strategies'].every((strategy) => typeof strategy === 'string' && TUNING_STRATEGY_IDS.includes(strategy));
}
